import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Droplet, Sparkles, Scissors } from "lucide-react";
import { Hero } from "@/components/ui/Hero";
import { ProductCard, ProductCardSkeleton } from "@/components/ui/ProductCard";
import { apiFetch, type Announcement } from "@/lib/api";
import { useVariantImagesMap } from "@/lib/variantImages";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useLanguage } from "@/hooks/useLanguage";
import { pickLocalized } from "@/lib/localized";
import {
  clearProductOrigin,
  rememberProductOrigin,
  takeProductOriginScroll,
} from "@/lib/productNavigation";
import herbacolLogo from "@/assets/herbacol_logo.png";
import bellLogo from "@/assets/bell_logo.png";
import lissiaLogo from "@/assets/lisia.png";

type Product = {
  id: string;
  name: string;
  name_en: string | null;
  slug: string;
  brand: { name: string };
  announcement: {
    id: string;
    message: string;
    message_en: string | null;
    text_color: string | null;
    background_color: string | null;
  } | null;
  variant_ids: number[];
  variant_announcements?: {
    id: number;
    announcement: Announcement | null;
  }[];
};

// Backend's max page size (routers/products.py caps `limit` at 100); every
// announced product is shown, so pages are fetched until `has_more` is false.
const ANNOUNCED_PRODUCTS_PAGE_SIZE = 100;
const ANNOUNCED_PRODUCTS_STALE_TIME = 60_000;

export default function Home() {
  const { t } = useTranslation();
  usePageTitle();
  const { currentLanguage } = useLanguage();
  const location = useLocation();
  const [visibleVariantIndexes, setVisibleVariantIndexes] = useState<Record<string, number>>({});

  // Navigating to / (e.g. the navbar logo) from a scrolled-down page should
  // land at the top, not wherever the browser happens to leave the scroll
  // position - react-router doesn't reset scroll on its own outside of
  // ScrollRestoration, which we don't use.
  // react-query (not local state) so the cache survives unmount/remount -
  // navigating to /products and back reuses the cached page instead of
  // flashing skeletons again while it refetches in the background.
  const { data: products, isError } = useQuery({
    queryKey: ["products", "announced"],
    queryFn: async () => {
      const all: Product[] = [];
      for (let offset = 0, hasMore = true; hasMore; offset += ANNOUNCED_PRODUCTS_PAGE_SIZE) {
        const res = await apiFetch(
          `/products/announced?offset=${offset}&limit=${ANNOUNCED_PRODUCTS_PAGE_SIZE}`
        );
        if (!res.ok) throw new Error("Could not load announced products");
        const page: { items: Product[]; has_more: boolean } = await res.json();
        all.push(...page.items);
        hasMore = page.has_more;
      }
      return all;
    },
    staleTime: ANNOUNCED_PRODUCTS_STALE_TIME,
  });
  const error = isError ? t("home.couldNotLoadProducts") : null;

  // Once per navigation: a background refetch of `products` (e.g. after
  // reordering announcements) must not yank the page back to the top.
  const scrolledForKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (products === undefined) return;
    if (scrolledForKeyRef.current === location.key) return;
    scrolledForKeyRef.current = location.key;
    const currentPath = `${location.pathname}${location.search}`;
    const returnScrollY = takeProductOriginScroll(currentPath);
    window.scrollTo({ top: returnScrollY ?? 0, behavior: "auto" });
  }, [location.key, location.pathname, location.search, products]);

  // Shared with the product list and detail pages via react-query, so a
  // picture already resolved there renders here instantly too (and vice
  // versa), with the actual fetch deduped/batched by the same loader.
  const imageUrls = useVariantImagesMap(
    products?.flatMap(p =>
      (p.variant_announcements ?? []).filter(v => v.announcement !== null).map(v => v.id)
    ) ?? []
  );

  const openAllProductsFromTop = () => {
    clearProductOrigin();
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  return (
    <div className="flex flex-col gap-12 max-w-[1280px] mx-auto w-full pb-10">
      <div className="home-hero-opening">
        <Hero />
      </div>

      <div className="home-shortcuts grid grid-cols-2 gap-3 px-2">
        <Link
          to="/products?brand=Herbacol"
          className="relative flex items-center justify-center gap-3 rounded-2xl bg-gradient-to-b from-brand-dark to-brand-dark/90 shadow-md hover:brightness-110 hover:scale-[1.02] hover:z-10 transition-all duration-200 p-4 h-20 sm:h-24"
        >
          <img src={herbacolLogo} alt="Herbacol" className="h-12 sm:h-14 w-auto object-contain" draggable={false} />
          <span className="text-white font-semibold text-lg">Herbacol</span>
        </Link>
        <Link
          to={`/products?category=${encodeURIComponent("Shampoo")}`}
          className="relative flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-b from-brand to-brand/85 text-white font-semibold text-lg shadow-md hover:brightness-110 hover:scale-[1.02] hover:z-10 transition-all duration-200 h-20 sm:h-24"
        >
          <Droplet className="size-7" />
          {t("home.shopShampoo")}
        </Link>
        <Link
          to="/products?brand=Bell+Franz"
          className="relative flex items-center justify-center gap-3 rounded-2xl bg-gradient-to-b from-brand-dark to-brand-dark/90 shadow-md hover:brightness-110 hover:scale-[1.02] hover:z-10 transition-all duration-200 p-4 h-20 sm:h-24"
        >
          <img src={bellLogo} alt="Bell Franz" className="h-12 sm:h-14 w-auto object-contain" draggable={false} />
          <span className="text-white font-semibold text-lg">Bell Franz</span>
        </Link>
        <Link
          to={`/products?category=${encodeURIComponent("Exfoliantes para Piel")}`}
          className="relative flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-b from-brand to-brand/85 text-white font-semibold text-lg shadow-md hover:brightness-110 hover:scale-[1.02] hover:z-10 transition-all duration-200 h-20 sm:h-24"
        >
          <Sparkles className="size-7" />
          {t("home.shopExfoliante")}
        </Link>
        <Link
          to="/products?brand=Lissia"
          className="relative flex items-center justify-center gap-3 rounded-2xl bg-gradient-to-b from-brand-dark to-brand-dark/90 shadow-md hover:brightness-110 hover:scale-[1.02] hover:z-10 transition-all duration-200 p-4 h-20 sm:h-24"
        >
          <img src={lissiaLogo} alt="Lissia" className="h-12 sm:h-14 w-auto object-contain" draggable={false} />
          <span className="text-white font-semibold text-lg">Lissia</span>
        </Link>
        <Link
          to="/products?category=Depilaci%C3%B3n"
          className="relative flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-b from-brand to-brand/85 text-white font-semibold text-lg shadow-md hover:brightness-110 hover:scale-[1.02] hover:z-10 transition-all duration-200 h-20 sm:h-24"
        >
          <Scissors className="size-7" />
          {t("home.shopDepilacion")}
        </Link>
        <Link
          to="/products"
          onClick={openAllProductsFromTop}
          className="col-span-2 w-full rounded-3xl flex items-center justify-center bg-gradient-to-b from-brand to-brand/85 text-white font-semibold text-lg py-4 shadow-md hover:brightness-110 transition-all duration-300"
        >
          {t("home.viewAllProducts")}
        </Link>
      </div>

      <div className="flex flex-col gap-6 px-2">
        <h2 className="text-2xl font-bold text-brand">{t("home.promotions")}</h2>

        {error && <div className="text-red-500">{error}</div>}

        <ul className="grid grid-cols-1 min-[360px]:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-10 justify-items-center">
          {products === undefined
            ? Array.from({ length: 4 }, (_, i) => (
                <li key={i} className="w-full max-w-55">
                  <ProductCardSkeleton />
                </li>
              ))
            : products.map(product => {
                const variantAnnouncements = product.variant_announcements ?? [];
                // Home only shows variants that have an announcement.
                const orderedVariantIds = variantAnnouncements
                  .filter(variant => variant.announcement !== null)
                  .map(variant => variant.id);
                const announcementByVariantId = new Map(
                  variantAnnouncements.map(variant => [variant.id, variant.announcement])
                );
                const visibleVariantIndex = visibleVariantIndexes[product.id] ?? 0;
                const visibleVariantId = orderedVariantIds[visibleVariantIndex];

                return (
                  <li key={product.id} className="w-full max-w-55">
                    <Link
                      to={`/product/${product.slug}/p/${product.id}${
                        visibleVariantId === undefined ? "" : `?variant=${visibleVariantId}`
                      }`}
                      className="block w-full"
                      onClick={() =>
                        rememberProductOrigin(
                          `${location.pathname}${location.search}`,
                          window.scrollY
                        )
                      }
                      draggable={false}
                    >
                      <ProductCard
                        name={pickLocalized(product.name, product.name_en, currentLanguage)}
                        brand={product.brand.name}
                        announcement={product.announcement}
                        imageUrls={orderedVariantIds.map(id => imageUrls[id])}
                        variantAnnouncements={
                          variantAnnouncements.length > 0
                            ? orderedVariantIds.map(id => announcementByVariantId.get(id) ?? null)
                            : undefined
                        }
                        onVisibleVariantChange={index =>
                          setVisibleVariantIndexes(previous => ({
                            ...previous,
                            [product.id]: index,
                          }))
                        }
                      />
                    </Link>
                  </li>
                );
              })}
        </ul>

        <Link
          to="/products"
          onClick={openAllProductsFromTop}
          className="w-full rounded-3xl flex items-center justify-center bg-gradient-to-b from-brand to-brand/85 text-white font-semibold text-lg py-4 shadow-md hover:brightness-110 transition-all duration-300"
        >
          {t("home.viewAllProducts")}
        </Link>
      </div>
    </div>
  );
}
